"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { useState } from "react";
export default function Assessment() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return;
}
function QuizContent() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    resetForm,
    watch
  } = useWvcForm();
  const [showLeadMagnet, setShowLeadMagnet] = useState(false);
  const [resultType, setResultType] = useState<"foundation" | "optimization" | "acceleration">("foundation");
  const formValues = watch();
  const isQuizComplete = formValues.industry_type && formValues.financial_visibility && formValues.tech_adoption && formValues.growth_priority;
  const onQuizSubmit = async () => {
    if (formValues.financial_visibility === "I only see numbers at tax time" || formValues.tech_adoption === "Manual entry & spreadsheets") {
      setResultType("foundation");
    } else if (formValues.tech_adoption === "Cloud software (QBO/Xero) but messy data") {
      setResultType("optimization");
    } else {
      setResultType("acceleration");
    }
    setShowLeadMagnet(true);
  };
  if (isSubmitted && showLeadMagnet) {
    return <Card className="w-full border-primary/20 bg-card/50 backdrop-blur-sm rounded-lg shadow-xl overflow-hidden">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2 text-accent mb-2">
            <CheckCircle2 className="size-5" />
            <span className="text-xs font-bold uppercase tracking-widest">Assessment Complete</span>
          </div>
          <CardTitle className="font-serif text-3xl">
            {resultType === "foundation" && "Foundation Phase"}
            {resultType === "optimization" && "Optimization Phase"}
            {resultType === "acceleration" && "Acceleration Phase"}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {resultType === "foundation" && "Your current systems are creating a bottleneck. We need to architect a clean financial foundation before you can safely scale."}
            {resultType === "optimization" && "You have the right tools, but the data isn't working for you yet. We'll focus on cleaning your workflows and unlocking real-time insights."}
            {resultType === "acceleration" && "You're ready for high-level advisory. It's time to shift from compliance to strategic tax planning and fleet/inventory optimization."}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="h-px bg-border mb-6" />
          <p className="text-sm font-bold mb-4">Ready for your custom blueprint? Leave your email below, and I'll send you a 3-point checklist tailored for {formValues.industry_type || "your business"} in Alberta.</p>
          <Form onSubmit={handleSubmit}>
            <div className="flex flex-col sm:flex-row gap-3">
              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^\S+@\S+$/i,
                message: "Invalid email"
              }
            }} render={({
              field
            }) => <FormItem className="flex-1">
                    <FormControl>
                      <Input placeholder="your@email.com" className="h-12 bg-background border-primary/30 focus:border-primary rounded-lg" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <Button type="submit" disabled={isSubmitting} className="h-12 px-8 bg-accent text-accent-foreground hover:bg-accent/90 rounded-lg font-bold transition-all">
                {isSubmitting ? <Loader className="animate-spin" /> : "Send My Results"}
              </Button>
            </div>
          </Form>
          <p className="mt-4 text-[10px] text-muted-foreground uppercase tracking-widest text-center">
            Secure & Confidential • Alberta-Based Advisory
          </p>
        </CardContent>
      </Card>;
  }
  if (isSubmitted && !showLeadMagnet) {
    return <div className="text-center p-12 bg-accent/5 rounded-lg border border-accent/20">
        <CheckCircle2 className="size-12 text-accent mx-auto mb-4" />
        <h3 className="text-2xl font-serif mb-2">Your Scale-Ready Report has been emailed to {formValues.email}.</h3>
        <p className="text-muted-foreground mb-6">Check your inbox for your personalized assessment.</p>
        <Button variant="outline" onClick={() => {
        resetForm();
        setShowLeadMagnet(false);
      }} className="rounded-lg border-primary text-primary">
          Take Quiz Again
        </Button>
      </div>;
  }
  return <Form onSubmit={onQuizSubmit} className="space-y-10 w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="industry_type" rules={{
        required: "Please select an option"
      }} render={({
        field
      }) => <FormItem className="space-y-4">
              <div className="space-y-1">
                <FormLabel className="text-sm font-bold uppercase tracking-widest text-primary">01. Industry Focus</FormLabel>
                <p className="text-xs text-muted-foreground font-medium">What is your primary business sector?</p>
              </div>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="grid grid-cols-1 gap-2">
                  {["Trades & Construction", "Professional Services", "Retail & E-commerce", "SaaS & IT Consulting"].map((option, i) => <FormItem key={option} data-index={i} className="flex items-center space-x-3 space-y-0 border border-border p-3 rounded-lg hover:bg-accent/5 transition-colors cursor-pointer">
                      <FormControl>
                        <RadioGroupItem value={option} />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer w-full">{option}</FormLabel>
                    </FormItem>)}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="financial_visibility" rules={{
        required: "Please select an option"
      }} render={({
        field
      }) => <FormItem className="space-y-4">
              <div className="space-y-1">
                <FormLabel className="text-sm font-bold uppercase tracking-widest text-primary">02. Financial Visibility</FormLabel>
                <p className="text-xs text-muted-foreground font-medium">How often do you review your numbers?</p>
              </div>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="grid grid-cols-1 gap-2">
                  {["I only see numbers at tax time", "Monthly, but I don't fully trust them", "Weekly KPI tracking", "Real-time dashboard visibility"].map((option, i) => <FormItem key={option} data-index={i} className="flex items-center space-x-3 space-y-0 border border-border p-3 rounded-lg hover:bg-accent/5 transition-colors cursor-pointer">
                      <FormControl>
                        <RadioGroupItem value={option} />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer w-full">{option}</FormLabel>
                    </FormItem>)}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="tech_adoption" rules={{
        required: "Please select an option"
      }} render={({
        field
      }) => <FormItem className="space-y-4">
              <div className="space-y-1">
                <FormLabel className="text-sm font-bold uppercase tracking-widest text-primary">03. Tech Stack</FormLabel>
                <p className="text-xs text-muted-foreground font-medium">How do you manage your bookkeeping?</p>
              </div>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="grid grid-cols-1 gap-2">
                  {["Manual entry & spreadsheets", "Cloud software (QBO/Xero) but messy data", "Fully automated with integrated apps", "Outsourced to a traditional firm"].map((option, i) => <FormItem key={option} data-index={i} className="flex items-center space-x-3 space-y-0 border border-border p-3 rounded-lg hover:bg-accent/5 transition-colors cursor-pointer">
                      <FormControl>
                        <RadioGroupItem value={option} />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer w-full">{option}</FormLabel>
                    </FormItem>)}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="growth_priority" rules={{
        required: "Please select an option"
      }} render={({
        field
      }) => <FormItem className="space-y-4">
              <div className="space-y-1">
                <FormLabel className="text-sm font-bold uppercase tracking-widest text-primary">04. Growth Priority</FormLabel>
                <p className="text-xs text-muted-foreground font-medium">What is your #1 goal for the next 12 months?</p>
              </div>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="grid grid-cols-1 gap-2">
                  {["Reducing tax liability", "Improving cash flow", "Scaling operations/team", "Preparing for exit/sale"].map((option, i) => <FormItem key={option} data-index={i} className="flex items-center space-x-3 space-y-0 border border-border p-3 rounded-lg hover:bg-accent/5 transition-colors cursor-pointer">
                      <FormControl>
                        <RadioGroupItem value={option} />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer w-full">{option}</FormLabel>
                    </FormItem>)}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="flex flex-col items-center gap-6 pt-6">
        <Button type="submit" disabled={!isQuizComplete} className="h-[56px] px-12 rounded-lg bg-accent text-accent-foreground font-default font-bold tracking-wider hover:bg-accent/90 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed group">
          Analyze My Architecture
          <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
        </Button>
        
        <div className="flex items-center gap-4">
          <div className="h-px w-8 bg-border" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Or</span>
          <div className="h-px w-8 bg-border" />
        </div>

        <Button asChild variant="outline" className="h-[48px] px-8 rounded-lg bg-transparent border-primary text-primary font-default font-semibold hover:bg-primary hover:text-primary-foreground transition-all duration-200">
          <Link to="#contact">Consult an Advisor</Link>
        </Button>
      </div>
    </Form>;
}